"use client";

import type { MouseEvent } from "react";
import { StandardButton } from "@/components/common/StandardButton";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";
import type { Order } from "@/types/models/order";
import { Check, X } from "lucide-react";
import { toast } from "sonner";

interface OrderActionsCellProps {
  order: Order;
}

type OrderSet = Order["order_sets"][number];

const getOrderSetStatus = (orderSet: OrderSet) =>
  orderSet.current_shipping_stage || orderSet.shipping_status || "";

const isRejected = (orderSet: OrderSet) =>
  getOrderSetStatus(orderSet) === "REJECTED";

const isCancelled = (orderSet: OrderSet) =>
  getOrderSetStatus(orderSet) === "CANCELLED";

const formatCount = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

const OrderActionsCell = ({ order }: OrderActionsCellProps) => {
  const dispatch = useAppDispatch();
  const { loadingById } = useAppSelector((state) => state.order);
  const isLoading = loadingById[order.id] || false;

  const orderSets = order.order_sets || [];
  const setsNeedingVerification = orderSets.filter(
    (orderSet) => orderSet.needs_verification,
  );
  const rejectedSets = orderSets.filter(isRejected);
  const cancelledSets = orderSets.filter(isCancelled);
  const acceptedSets = orderSets.filter(
    (orderSet) =>
      !orderSet.needs_verification &&
      !isRejected(orderSet) &&
      !isCancelled(orderSet),
  );
  const hasUnverifiedSets =
    order.current_order_stage !== "CANCELLED" &&
    (setsNeedingVerification.length > 0 ||
      rejectedSets.length > 0 ||
      Boolean(order.needs_seller_verification));

  const handleAcceptOrder = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const verificationTarget = setsNeedingVerification.length || orderSets.length;
    const warningLines = [
      `Accept this order? This will accept ${
        verificationTarget > 0
          ? formatCount(verificationTarget, "order set")
          : "the order sets"
      } that still need verification.`,
      rejectedSets.length > 0
        ? `${formatCount(
            rejectedSets.length,
            "order set",
          )} were previously rejected and will also be accepted.`
        : null,
    ].filter(Boolean);

    if (!window.confirm(warningLines.join("\n\n"))) return;

    if (rejectedSets.length > 0) {
      toast.warning(
        `${formatCount(
          rejectedSets.length,
          "order set",
        )} were previously rejected and will be accepted now.`,
      );
    }

    dispatch(orderActions.approveOrder(order.id));
  };

  const handleCancelOrder = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const warningLines = [
      `Cancel this order? This will cancel every order set${
        orderSets.length > 0 ? ` (${formatCount(orderSets.length, "set")})` : ""
      }.`,
      acceptedSets.length > 0
        ? `${formatCount(
            acceptedSets.length,
            "order set",
          )} were previously accepted or verified and will also be cancelled.`
        : null,
      rejectedSets.length > 0
        ? `${formatCount(
            rejectedSets.length,
            "order set",
          )} were already rejected and are included in this order cancellation.`
        : null,
      cancelledSets.length > 0
        ? `${formatCount(
            cancelledSets.length,
            "order set",
          )} were already cancelled and are included in this order cancellation.`
        : null,
    ].filter(Boolean);

    if (!window.confirm(warningLines.join("\n\n"))) return;

    toast.warning("Cancelling this order will cancel every order set.");
    dispatch(orderActions.rejectOrder(order.id));
  };

  if (!hasUnverifiedSets) {
    return <span className="text-xs text-muted-foreground">All sets verified</span>;
  }

  return (
    <div className="flex gap-1 items-center">
      <StandardButton
        variant="success"
        size="sm"
        onClick={handleAcceptOrder}
        disabled={isLoading}
        className="h-8 px-2 text-xs gap-1 flex-shrink-0"
      >
        <Check className="w-4 h-4" />
        {isLoading ? "Processing..." : "Accept"}
      </StandardButton>
      <StandardButton
        variant="destructive"
        size="sm"
        onClick={handleCancelOrder}
        disabled={isLoading}
        className="h-8 px-2 text-xs gap-1 flex-shrink-0"
      >
        <X className="w-4 h-4" />
        {isLoading ? "Processing..." : "Cancel"}
      </StandardButton>
    </div>
  );
};

export default OrderActionsCell;
